<template>
  <section class="overview-page">
    <header class="hero">
      <img alt="Vue logo" class="hero__logo" src="../assets/logo.png">
      <div>
        <p class="eyebrow">Vue2 Micro App</p>
        <h1>Overflow Container Demo</h1>
        <p class="hero__copy">
          This page intentionally renders more content so you can verify scrolling stays inside the qiankun host layout.
        </p>
      </div>
    </header>

    <section class="summary-grid">
      <article class="summary-card" v-for="metric in metrics" :key="metric.label">
        <p class="summary-card__label">{{ metric.label }}</p>
        <strong class="summary-card__value">{{ metric.value }}</strong>
      </article>
    </section>

    <section class="stack">
      <article class="panel" v-for="section in sections" :key="section.title">
        <div class="panel__header">
          <div>
            <p class="eyebrow">{{ section.kicker }}</p>
            <h2>{{ section.title }}</h2>
          </div>
          <span class="panel__badge">{{ section.items.length }} items</span>
        </div>

        <p class="panel__copy">{{ section.description }}</p>

        <ul class="panel__list">
          <li class="panel__item" v-for="item in section.items" :key="item.title">
            <div>
              <strong>{{ item.title }}</strong>
              <p>{{ item.description }}</p>
            </div>
            <span>{{ item.status }}</span>
          </li>
        </ul>
      </article>
    </section>
  </section>
</template>

<script>
export default {
  name: 'OverviewView',
  data() {
    return {
      metrics: [
        { label: 'Container Mode', value: 'Shared Scroll' },
        { label: 'App Shell', value: 'Qiankun Host' },
        { label: 'Framework', value: 'Vue 2 + Router' },
      ],
      sections: [
        {
          kicker: 'Queue',
          title: 'Pending work items',
          description: 'A longer content block to make sure the embedded app works well in the host content area.',
          items: [
            { title: 'Refactor dashboard widgets', description: 'Move the widgets into isolated components with clearer ownership.', status: 'In review' },
            { title: 'Audit style leakage', description: 'Check shared selectors and verify host layout remains stable.', status: 'Ready' },
            { title: 'Document local dev flow', description: 'Capture startup order and common gotchas for the team.', status: 'Queued' },
            { title: 'Verify route transitions', description: 'Make sure repeated mount and unmount cycles stay clean.', status: 'Testing' },
            { title: 'Add regression cases', description: 'Cover container overflow and repeated navigation behavior.', status: 'Planned' },
          ],
        },
        {
          kicker: 'Timeline',
          title: 'Deployment checklist',
          description: 'These rows intentionally stack to create vertical overflow inside the child application page.',
          items: [
            { title: 'Build host app', description: 'Confirm the shell registers both child apps and routes correctly.', status: 'Done' },
            { title: 'Publish React child', description: 'Ensure CRA output is exposed as a UMD bundle.', status: 'Done' },
            { title: 'Publish Vue2 child', description: 'Validate publicPath and runtime mount/unmount hooks.', status: 'Blocked' },
            { title: 'Smoke test navigation', description: 'Enter, leave, and re-enter each child app multiple times.', status: 'Queued' },
            { title: 'Capture screenshots', description: 'Record the overflow behavior for future regression checks.', status: 'Queued' },
            { title: 'Review performance budget', description: 'Measure initial mount cost and asset loading.', status: 'Queued' },
          ],
        },
        {
          kicker: 'Notes',
          title: 'Observed runtime states',
          description: 'Use this page to check whether the host layout remains fixed while the child route content scrolls naturally.',
          items: [
            { title: 'Host sidebar stays fixed', description: 'Only the content pane should move while scrolling.', status: 'Expected' },
            { title: 'Child app owns route state', description: 'The hash route should persist while staying inside the host.', status: 'Expected' },
            { title: 'Styles stay isolated', description: 'No child selector should affect the shell root node.', status: 'Expected' },
            { title: 'Unmount stays clean', description: 'Leaving the route should remove child DOM and listeners.', status: 'Expected' },
            { title: 'Re-entry stays stable', description: 'Revisiting the route should render the same layout again.', status: 'Expected' },
            { title: 'Overflow remains visible', description: 'This final block helps ensure there is enough vertical content.', status: 'Expected' },
          ],
        },
      ],
    }
  },
}
</script>

<style scoped>
.overview-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.hero {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  align-items: center;
  text-align: left;
}

.hero__logo {
  width: 72px;
  height: 72px;
}

.hero__copy {
  margin-top: 0.35rem;
  color: #52616f;
}

.eyebrow {
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #42b983;
  margin-bottom: 0.35rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

.summary-card,
.panel {
  border: 1px solid #dbe7df;
  border-radius: 14px;
  background: #f8fbf9;
}

.summary-card {
  padding: 1rem;
  text-align: left;
}

.summary-card__label {
  font-size: 0.85rem;
  color: #60707d;
}

.summary-card__value {
  display: block;
  margin-top: 0.25rem;
  font-size: 1.05rem;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.panel {
  padding: 1rem;
  text-align: left;
}

.panel__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.panel__badge {
  border-radius: 999px;
  background: #e4f4ec;
  color: #2d7b5c;
  padding: 0.2rem 0.65rem;
  font-size: 0.82rem;
  white-space: nowrap;
}

.panel__copy {
  margin-top: 0.5rem;
  color: #52616f;
}

.panel__list {
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.panel__item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding-top: 0.75rem;
  border-top: 1px solid #dbe7df;
}

.panel__item p {
  margin-top: 0.25rem;
  color: #60707d;
}

.panel__item span {
  color: #2d7b5c;
  font-size: 0.88rem;
  white-space: nowrap;
}

@media (max-width: 640px) {
  .hero {
    grid-template-columns: 1fr;
  }

  .panel__header,
  .panel__item {
    flex-direction: column;
  }
}
</style>
